import React from "react";

import { getApiUrl } from "@/config";
import InteractionService, { InteractionApi } from "@/service/Interaction";
import LocalStorageService from "@/service/LocalStorage";
import { Opinion, OpinionResult } from "@/service/Model";
import TrackingService from "@/service/Tracking";
import { scaleRange } from "@/utils/transform";

export type StoredOpinion = {
  answer?: number;
  createdAt?: string;
};

export type OpinionProviderProps = {
  interaction: Opinion;
  interactionResults?: OpinionResult;
  preview: boolean;
};

export type OpinionProviderState = {
  opinionValue?: StoredOpinion;
  isPreview: boolean;
  onThumbsAnswer: Function;
  onMeterAnswer: Function;
  onNpsAnswer: Function;
  onNpsFeedback: Function;
  onNpsClose: Function;
  onRatingAnswer: Function;
};

const OpinionContext = React.createContext<OpinionProviderState>({
  opinionValue: undefined,
  isPreview: false,
  onThumbsAnswer: () => null,
  onMeterAnswer: () => null,
  onNpsAnswer: () => null,
  onNpsFeedback: () => null,
  onNpsClose: () => null,
  onRatingAnswer: () => null,
});

export class OpinionProvider extends React.Component<
  OpinionProviderProps,
  OpinionProviderState
> {
  private api: InteractionApi;

  public constructor(props: OpinionProviderProps) {
    super(props);

    this.handleOnThumbsAnswer = this.handleOnThumbsAnswer.bind(this);
    this.handleOnMeterAnswer = this.handleOnMeterAnswer.bind(this);
    this.handleOnNpsAnswer = this.handleOnNpsAnswer.bind(this);
    this.handleOnRatingAnswer = this.handleOnRatingAnswer.bind(this);
    this.handleOnNpsFeedback = this.handleOnNpsFeedback.bind(this);
    this.handleOnNpsClose = this.handleOnNpsClose.bind(this);
    this.onVote = this.onVote.bind(this);
    this.state = {
      opinionValue:
        !props.preview && props.interaction && props.interaction.id
          ? LocalStorageService.getOpinion(props.interaction.id)
          : undefined,
      isPreview: props.preview,
      onThumbsAnswer: this.handleOnThumbsAnswer,
      onMeterAnswer: this.handleOnMeterAnswer,
      onNpsAnswer: this.handleOnNpsAnswer,
      onNpsFeedback: this.handleOnNpsFeedback,
      onNpsClose: this.handleOnNpsClose,
      onRatingAnswer: this.handleOnRatingAnswer,
    };
    this.api = new InteractionService(getApiUrl(props.preview));
  }

  public render(): JSX.Element {
    return (
      <OpinionContext.Provider value={{ ...this.state }}>
        {this.props.children}
      </OpinionContext.Provider>
    );
  }

  private async handleOnThumbsAnswer(
    interactionId: string,
    interactionResults: OpinionResult,
    answer: number
  ): Promise<void> {
    const value: number[] = [];
    const { isPreview } = this.state;

    for (let i = 0; i < interactionResults.value.length; i++) {
      value.push(0);
    }

    const storedOpinion = LocalStorageService.getOpinion(interactionId);
    if (storedOpinion && storedOpinion.answer !== undefined) {
      value[storedOpinion.answer] -= 1;
    }

    value[answer] += 1;

    if (!isPreview) {
      this.api.postOpinionResults(interactionId, value);
      LocalStorageService.setOpinion(interactionId, { answer });
      TrackingService.getInstance().trackOnVote();
    }

    this.setState({
      opinionValue: { answer },
    });
  }

  private handleOnNpsClose(interactionId: string): void {
    const { isPreview } = this.state;
    if (!isPreview && !LocalStorageService.getOpinion(interactionId)) {
      LocalStorageService.setOpinion(interactionId, {
        createdAt: new Date().toISOString(),
      });
    }
  }

  private handleOnNpsAnswer(interactionId: string, opinion: number): void {
    this.onVote(interactionId, opinion, 11);
  }

  private handleOnRatingAnswer(interactionId: string, opinion: number): void {
    this.onVote(interactionId, opinion, 5);
  }

  private onVote(
    interactionId: string,
    opinion: number,
    defaultLength: number
  ): void {
    const { isPreview } = this.state;
    // The opinion is the position of the array, which is marked with the number "1"
    const opinionArray = Array(defaultLength).fill(0);
    opinionArray[opinion] = 1;

    // This is relevant in case the user wants to change his mind,
    // same behavior as thumbs/meter (will probably never happen for nps)
    const storedOpinion = LocalStorageService.getOpinion(interactionId);
    if (storedOpinion && storedOpinion.answer !== undefined) {
      // Reset old opinion with "-1" (Attention: backend deducts the old user opinion (anonymous))
      opinionArray[storedOpinion.answer] -= 1;
    }

    if (!isPreview) {
      this.api.postOpinionResults(interactionId, opinionArray);
      // Notice: Don't wait for api response (even if it fails), UI can render immediately
      const createdAt = new Date().toISOString();
      LocalStorageService.setOpinion(interactionId, {
        answer: opinion,
        createdAt,
      });
      TrackingService.getInstance().trackOnVote();
    }

    this.setState({
      opinionValue: { answer: opinion },
    });
  }

  private handleOnNpsFeedback(
    opinionId: string,
    answer: number,
    message: string
  ): void {
    const { isPreview } = this.state;
    if (!isPreview) {
      this.api.postOpinionFeedback(opinionId, answer, message);
    }
  }

  private async handleOnMeterAnswer(
    interactionId: string,
    interactionResults: OpinionResult,
    opinion: {
      answer: number;
      minAnswer: number;
      maxAnswer: number;
      bucketsCount: number;
    }
  ): Promise<void> {
    const { isPreview } = this.state;
    const value: number[] = [];

    for (let i = 0; i < interactionResults.value.length; i++) {
      value.push(0);
    }

    const storedOpinion = LocalStorageService.getOpinion(interactionId);
    if (storedOpinion && storedOpinion.answer !== undefined) {
      const oldAnswerBucket = Math.round(
        scaleRange(
          storedOpinion.answer,
          opinion.minAnswer,
          opinion.maxAnswer,
          0,
          opinion.bucketsCount - 1
        )
      );
      value[oldAnswerBucket] -= 1;
    }

    const answerBucket = Math.round(
      scaleRange(
        opinion.answer,
        opinion.minAnswer,
        opinion.maxAnswer,
        0,
        opinion.bucketsCount - 1
      )
    );
    value[answerBucket] += 1;

    if (!isPreview) {
      if (value[answerBucket] === 1) {
        this.api.postOpinionResults(interactionId, value);
      }
      LocalStorageService.setOpinion(interactionId, {
        answer: opinion.answer,
      });
      TrackingService.getInstance().trackOnVote();
    }
  }
}

export const OpinionConsumer = OpinionContext.Consumer;
export default OpinionContext;
